import { create } from 'zustand'
import { trackById, type Lesson } from './tracks'

/**
 * Per-user Learn progress: which lessons are done and the active track/position.
 *
 * SOURCE OF TRUTH is the server (per-user, part of the account) so progress
 * survives a new device / cache clear, mirroring how `themePreference` syncs via
 * useThemeServerSync into edge-user-api. localStorage here is only an OPTIMISTIC
 * CACHE: it hydrates the UI instantly on load and buffers writes, but a server
 * sync (next increment) hydrates authoritative state via `hydrate()` and
 * write-throughs each change. State is bounded by construction (a finite set of
 * lesson slugs + one pointer), so the cache key stays tiny. Pure selectors are
 * exported alongside so they're unit-testable without the store.
 */

const KEY = 'edge:learn:progress'

// A `type` (not interface) so it's assignable to Record<string, unknown> when
// synced through the user-profile update mutation (interfaces lack the implicit
// index signature; type aliases have it). Mirrors ChartInteractionSettings.
export type LearnProgress = {
  /** Slugs of lessons the user has finished. */
  completed: string[]
  /** The track currently in progress, or null. Drives the bottom-left bar. */
  activeTrackId: string | null
  /** Position within the active track's lesson list. */
  activeLessonIndex: number
}

const EMPTY: LearnProgress = { completed: [], activeTrackId: null, activeLessonIndex: 0 }

function readPersisted(): LearnProgress {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return EMPTY
    const parsed = JSON.parse(raw) as Partial<LearnProgress>
    return {
      completed: Array.isArray(parsed.completed)
        ? parsed.completed.filter((s): s is string => typeof s === 'string')
        : [],
      activeTrackId: typeof parsed.activeTrackId === 'string' ? parsed.activeTrackId : null,
      activeLessonIndex:
        typeof parsed.activeLessonIndex === 'number' ? parsed.activeLessonIndex : 0,
    }
  } catch {
    return EMPTY
  }
}

function persist(p: LearnProgress): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p))
  } catch {
    /* ignore quota / unavailable storage */
  }
}

// ── Pure selectors (testable without the store) ──────────────────────────────

/** Has this lesson been completed? */
export function lessonDone(completed: readonly string[], slug: string): boolean {
  return completed.includes(slug)
}

/** How many of these lessons are complete. */
export function countDone(completed: readonly string[], lessons: readonly Lesson[]): number {
  return lessons.reduce((n, l) => (completed.includes(l.slug) ? n + 1 : n), 0)
}

/** Index of the first not-yet-complete lesson, or the list length if all done. */
export function firstIncompleteIndex(
  completed: readonly string[],
  lessons: readonly Lesson[],
): number {
  const i = lessons.findIndex((l) => !completed.includes(l.slug))
  return i === -1 ? lessons.length : i
}

// ── Store ────────────────────────────────────────────────────────────────────

interface LearnProgressState extends LearnProgress {
  /**
   * Replace local state with authoritative progress from the server (on login /
   * initial sync). Also refreshes the optimistic cache. The server sync layer
   * (next increment) calls this after fetching the user's account record.
   */
  hydrate: (progress: LearnProgress) => void
  /** Begin (or resume) a track: positions at its first incomplete lesson. */
  startTrack: (trackId: string) => void
  /** Mark a lesson complete (idempotent). */
  completeLesson: (slug: string) => void
  /** Remove one slug (idempotent). Used for flag slugs such as a dismissed
   *  checklist, so a person can bring it back. */
  uncompleteLesson: (slug: string) => void
  /** Move the active pointer to the next incomplete lesson, or end the track. */
  advanceTrack: () => void
  /** Clear the active track (progress is kept). */
  endTrack: () => void
  /** Wipe all progress (restart / tests). */
  reset: () => void
}

export const useLearnProgressStore = create<LearnProgressState>((set, get) => ({
  ...readPersisted(),

  hydrate: (progress) => {
    const next: LearnProgress = {
      completed: [...progress.completed],
      activeTrackId: progress.activeTrackId,
      activeLessonIndex: progress.activeLessonIndex,
    }
    persist(next)
    set(next)
  },

  startTrack: (trackId) => {
    const track = trackById(trackId)
    if (!track) return
    const index = firstIncompleteIndex(get().completed, track.lessons)
    // All done → start at the top so a "restart" replays from the beginning.
    const activeLessonIndex = index >= track.lessons.length ? 0 : index
    const next = { ...get(), activeTrackId: trackId, activeLessonIndex }
    persist(next)
    set(next)
  },

  completeLesson: (slug) => {
    const { completed } = get()
    if (completed.includes(slug)) return
    const next = { ...get(), completed: [...completed, slug] }
    persist(next)
    set(next)
  },

  uncompleteLesson: (slug) => {
    const { completed } = get()
    if (!completed.includes(slug)) return
    const next = { ...get(), completed: completed.filter((s) => s !== slug) }
    persist(next)
    set(next)
  },

  advanceTrack: () => {
    const { activeTrackId, completed } = get()
    const track = activeTrackId ? trackById(activeTrackId) : undefined
    if (!track) return
    const index = firstIncompleteIndex(completed, track.lessons)
    if (index >= track.lessons.length) {
      // Whole track complete → clear the active pointer.
      const next = { ...get(), activeTrackId: null, activeLessonIndex: 0 }
      persist(next)
      set(next)
    } else {
      const next = { ...get(), activeLessonIndex: index }
      persist(next)
      set(next)
    }
  },

  endTrack: () => {
    const next = { ...get(), activeTrackId: null, activeLessonIndex: 0 }
    persist(next)
    set(next)
  },

  reset: () => {
    persist(EMPTY)
    set({ ...EMPTY })
  },
}))
